import { TextField, TextFieldProps } from "@mui/material";
import { join } from "lodash-es";
import React, { useCallback } from "react";
import { FieldValues, useController } from "react-hook-form";

import { SENSITIVE_CLASS_NAME } from "../../utils/highlightUtils";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type ITextFieldProps = Omit<TextFieldProps, "value" | "name" | "inputRef">;

interface IProps<TFieldValues extends FieldValues, TName extends FieldPathOfType<TFieldValues, string>>
    extends ITextFieldProps {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, string>;
}

export function ControllerTextField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, string> = FieldPathOfType<TFieldValues, string>,
>({
    name,
    controllerProps,
    onChange,
    onBlur,
    ...textFieldProps
}: IProps<TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { ref, onChange: onFieldChange, onBlur: onFieldBlur, value, ...field },
    } = useController({ ...controllerProps, name, defaultValue: controllerProps?.defaultValue ?? ("" as never) });

    const handleChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
        (...args) => {
            onFieldChange(...args);
            onChange?.(...args);
        },
        [onFieldChange, onChange],
    );
    const handleBlur = useCallback<NonNullable<TextFieldProps["onBlur"]>>(
        (...args) => {
            onFieldBlur();
            onBlur?.(...args);
        },
        [onFieldBlur, onBlur],
    );

    return (
        <TextField
            {...field}
            size="small"
            {...textFieldProps}
            inputRef={ref}
            onChange={handleChange}
            onBlur={handleBlur}
            value={value ?? ""}
            inputProps={{
                ...textFieldProps.inputProps,
                className: join([textFieldProps.inputProps?.className, SENSITIVE_CLASS_NAME], " "),
            }}
        />
    );
}
